// src/views/Seguridad/Permisos/CrearPermisos.jsx
import React from "react";
import { FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

// 👇 NUEVO
import {
  CRUD_ACTIONS,
  BUSINESS_ACTIONS_BY_MENU,
} from "../../../menu/actionsByMenu";

export default function CrearPermiso({
  onVolver = () => {},
  onCreated = () => {},
}) {
  // ===== Submódulos =====
  const [submods, setSubmods] = React.useState([]);
  const [loadingSubs, setLoadingSubs] = React.useState(false);
  const [errSubs, setErrSubs] = React.useState("");

  // Dropdown
  const [openSubs, setOpenSubs] = React.useState(false);
  const boxRef = React.useRef(null);
  const [searchSub, setSearchSub] = React.useState("");
  const [selectedSubIds, setSelectedSubIds] = React.useState([]);

  // ===== Acciones seleccionadas =====
  const [acciones, setAcciones] = React.useState({});

  // ===============================
  // 🔑 ACCIONES DISPONIBLES
  // ===============================
  const accionesDisponibles = React.useMemo(() => {
    if (selectedSubIds.length === 1) {
      const menuId = selectedSubIds[0];
      const business = BUSINESS_ACTIONS_BY_MENU[menuId] || [];
      return [...CRUD_ACTIONS, ...business];
    }
    return CRUD_ACTIONS;
  }, [selectedSubIds]);

  // Reset acciones si cambia selección de submódulos
  React.useEffect(() => {
    const next = {};
    accionesDisponibles.forEach((a) => {
      next[a.key] = false;
    });
    setAcciones(next);
  }, [accionesDisponibles]);

  const toggleAccion = (k) =>
    setAcciones((s) => ({ ...s, [k]: !s[k] }));

  const accionesSeleccionadas = React.useMemo(
    () => Object.entries(acciones).filter(([, v]) => v).map(([k]) => k),
    [acciones]
  );

  // ===== Lista acumulada =====
  const [pendientes, setPendientes] = React.useState([]);
  const [formErr, setFormErr] = React.useState("");

  // Guardado
  const [saving, setSaving] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState("");

  // ===== Helpers =====
  const labelSubmods =
    selectedSubIds.length === 0
      ? "Seleccionar"
      : selectedSubIds.length === 1
      ? "1 seleccionado"
      : `${selectedSubIds.length} seleccionados`;

  const filteredSubs = React.useMemo(() => {
    const q = searchSub.trim().toLowerCase();
    if (!q) return submods;
    return submods.filter((s) =>
      (s.nombre || "").toLowerCase().includes(q)
    );
  }, [submods, searchSub]);

  const toggleSub = (id) =>
    setSelectedSubIds((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );

  // ===== Cerrar dropdown click afuera =====
  React.useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target))
        setOpenSubs(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ===== Cargar submódulos =====
  React.useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      setLoadingSubs(true);
      setErrSubs("");
      try {
        const acc = [];
        let page = 0;
        const size = 200;
        while (true) {
          const url = apiRoutes.permisos.autocompleteUrl(
            "submodulo",
            "",
            page,
            size
          );
          const data = await apiFetchJson(url);
          const chunk = (data?.items || [])
            .map((it) => ({
              id: it.MENU_SQ_COD,
              nombre: it.MENU_DS_TITULO,
            }))
            .filter(
              (it) =>
                String(it.nombre || "")
                  .trim()
                  .toLowerCase() !== "inicio"
            );
          acc.push(...chunk);
          if (!data?.hasMore) break;
          page = (data?.page ?? page) + 1;
          if (cancelled) return;
        }
        if (!cancelled) setSubmods(acc);
      } catch (e) {
        if (!cancelled)
          setErrSubs(
            e?.message || "No se pudieron cargar los submódulos."
          );
      } finally {
        if (!cancelled) setLoadingSubs(false);
      }
    };
    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // ===== Agregar =====
  const agregar = () => {
    setFormErr("");
    if (selectedSubIds.length === 0) {
      setFormErr("Seleccioná al menos un submódulo.");
      return;
    }
    if (accionesSeleccionadas.length === 0) {
      setFormErr("Seleccioná al menos una acción.");
      return;
    }

    const byId = new Map(submods.map((s) => [s.id, s]));
    const nuevos = [];

    for (const sid of selectedSubIds) {
      const s = byId.get(sid);
      if (!s) continue;
      for (const perm of accionesSeleccionadas) {
        nuevos.push({
          submoduloId: s.id,
          submoduloNombre: s.nombre,
          permiso: perm,
        });
      }
    }

    setPendientes((prev) => {
      const key = (r) => `${r.submoduloId}|${r.permiso}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...nuevos.filter((r) => !seen.has(key(r)))];
    });
  };

  const remover = (idx) =>
    setPendientes((prev) => prev.filter((_, i) => i !== idx));
  const limpiarLista = () => setPendientes([]);

  // ===== Guardar =====
  const guardarLote = async () => {
    setFormErr("");
    setSaveErr("");
    if (pendientes.length === 0) {
      setFormErr("No hay elementos para guardar.");
      return;
    }

    const payload = pendientes.map((r) => ({
      menuSqCod: r.submoduloId,
      permiso: r.permiso,
    }));

    try {
      setSaving(true);
      const resp = await apiFetchJson(apiRoutes.permisos.create, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onCreated({
        count: Number(resp?.insertedCount ?? 0),
        metrics: resp,
      });

      setPendientes([]);
      onVolver();
    } catch (e) {
      setSaveErr(e?.message || "No se pudo guardar el lote.");
    } finally {
      setSaving(false);
    }
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <div className="page w-full max-w-4xl">
      <div className="mb-4 flex items-center gap-3">
        <button className="btn-fil" onClick={onVolver} disabled={saving}>
          <FaArrowLeft className="inline mr-2" /> Volver
        </button>
        <h2 className="page-title m-0">Crear Permiso</h2>
      </div>

      <div className="rounded-md border bg-white px-4 py-4">
        <div className="grid grid-cols-12 gap-4">
          {/* SUBMODULOS */}
          <div className="col-span-12 md:col-span-6">
            <label className="label-strong">Submódulo</label>
            <div ref={boxRef} className="relative mt-1">
              <button
                className="input w-full text-left"
                onClick={() => setOpenSubs((v) => !v)}
              >
                {labelSubmods}
              </button>

              {openSubs && (
                <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-80 overflow-auto">
                  {filteredSubs.map((op) => (
                    <label
                      key={op.id}
                      className="flex gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubIds.includes(op.id)}
                        onChange={() => toggleSub(op.id)}
                      />
                      {op.nombre}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {errSubs && (
              <div className="text-xs text-red-600 mt-1">{errSubs}</div>
            )}
          </div>

          {/* ACCIONES */}
          <div className="col-span-12 md:col-span-6">
            <label className="label-strong">Acciones</label>
            <div className="mt-1 grid grid-cols-2 gap-y-2">
              {accionesDisponibles.map((a) => {
                const Icon = a.icon;
                return (
                  <label key={a.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!acciones[a.key]}
                      onChange={() => toggleAccion(a.key)}
                    />
                    {Icon && <Icon className="text-gray-600" />}
                    <span>{a.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {formErr && <div className="text-red-600 mt-3">{formErr}</div>}

        <div className="mt-4 text-right">
          <button className="btn-fil" onClick={agregar}>
            Agregar
          </button>
        </div>
      </div>

      {/* LISTA */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold">Permisos a crear</h3>

        {pendientes.length === 0 ? (
          <div className="text-sm text-gray-600">No hay elementos.</div>
        ) : (
          <>
            <table className="table-card mt-2">
              <thead className="table-head">
                <tr>
                  <th className="cell">Submódulo</th>
                  <th className="cell">Permiso</th>
                  <th className="cell-center">
                    <button onClick={limpiarLista}>✕</button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((r, idx) => (
                  <tr key={idx}>
                    <td className="cell">{r.submoduloNombre}</td>
                    <td className="cell">{r.permiso}</td>
                    <td className="cell-center">
                      <button onClick={() => remover(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 text-right">
              <button
                className="btn-fil"
                onClick={guardarLote}
                disabled={saving}
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </>
        )}

        {saveErr && <div className="text-red-600 mt-3">{saveErr}</div>}
      </div>
    </div>
  );
}
